/**
 * The single source of truth for table cell content typography.
 *
 * Every populated data cell renders at one uniform treatment — `text-sm
 * text-foreground` — with no per-column color, size, or weight variation.
 * Empty values render a dim em-dash. Badges, links, avatars, and row-menu
 * cells are NOT cell text and keep their own treatment.
 *
 * `textCell` is the default for almost everything — text AND numbers-that-
 * aren't-money (identifiers like account #, deal #, zip, meter #; dates;
 * counts; volumes; percentages; terms). `monoCell` is RARE: it exists only
 * for columns of money amounts and rates, where digit/decimal alignment
 * across rows is the actual point. A value having digits does not make it a
 * `monoCell` — an account number is an identifier string, a date is a date,
 * neither aligns "as figures."
 *
 * `wrapTextCell` is for the few columns whose value is a sentence (a reason,
 * a note): it keeps the same typography but wraps instead of truncating.
 *
 * Reach for these helpers in every column `cell:` renderer. Do not author a
 * `<span className=...>` with font/size/color inside a cell — that is the
 * per-column drift this module exists to stop.
 */

/**
 * The default cell. Use for all text and for any number that is not money/rate
 * (identifiers, dates, counts, volumes, percentages, terms). Renders a dim
 * em-dash when absent; a legitimate `0` still renders.
 */
export function textCell(value: string | number | null | undefined) {
  return value === null || value === undefined || value === "" ? (
    <span className="text-text-dim">—</span>
  ) : (
    <span className="truncate text-sm text-foreground">{value}</span>
  );
}

/**
 * For a column whose value is a sentence or a long name, such as why a record
 * was not imported. Same typography as {@link textCell}, but the text wraps
 * onto as many lines as it needs instead of being cut off, so a reader sees
 * all of it. Short values stay {@link textCell}. Renders a dim em-dash when
 * absent.
 */
export function wrapTextCell(value: string | null | undefined) {
  return value === null || value === undefined || value === "" ? (
    <span className="text-text-dim">—</span>
  ) : (
    <span className="whitespace-normal break-words text-sm text-foreground">{value}</span>
  );
}

/**
 * RARE — money amounts and rates ONLY. Adds `tabular-nums` so a
 * column of dollar/rate figures aligns by digit. (The name predates the
 * app-wide move off the monospace face; the figures are sans now.) Do NOT use for identifiers,
 * dates, counts, volumes, or percentages — those are {@link textCell}.
 * Renders a dim em-dash when absent; a legitimate `0` still renders.
 */
export function monoCell(value: string | number | null | undefined) {
  return value === null || value === undefined || value === "" ? (
    <span className="text-text-dim">—</span>
  ) : (
    <span className="text-sm text-foreground tabular-nums">{value}</span>
  );
}
